import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Table from "../../components/common/table/Table";
import Pagination from "../../components/common/pagination/Pagination";
import Title from "../../components/common/title/Title";
import useTableRequest from "../../hooks/useTableRequest";
import { getApi, postApi } from "../../services/api/globalApi";
import { utilsState } from "../../sotre/redux/features/utilsSlice";
import { convertDate } from "../../utils/convertDate";
import { errorMessage, successMessage } from "../../utils/toastify-message/ToastifyMessage";

const modes = { all: "همهٔ کاربران", roles: "بر اساس نقش", users: "کاربران انتخابی", exceptUsers: "همهٔ کاربران به‌جز افراد انتخاب‌شده", legacy: "واریز گروهی پیشین" };
const number = (value) => Number(value || 0).toLocaleString("fa-IR");
const columns = [
  { title: "شناسه واریز", value: "identity" }, { title: "تاریخ", value: "date" },
  { title: "ثبت‌کننده", value: "admin" }, { title: "گیرندگان", value: "audience" },
  { title: "تعداد", value: "recipientCount" }, { title: "مبلغ هر نفر (تومان)", value: "individualAmount" },
  { title: "مبلغ کل (تومان)", value: "total" }, { title: "وضعیت", value: "state" },
];

export default function BulkDepositHistory() {
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get("batchId") || "";
  const request = useTableRequest();
  const loadHistory = request.load;
  const { utils } = useSelector(utilsState);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const dialog = useRef();
  const previewSequence = useRef(0);

  const load = useCallback((targetPage = page) => loadHistory({ path: "admin/wallets/deposit/bulk/history", params: { page: targetPage, limit: 20, ...(batchId ? { batchId } : {}) } }, response => {
    setRecords(response?.data?.result?.data || []);
    setPagination(response?.data?.result);
  }), [page, batchId, loadHistory]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { previewSequence.current += 1; }, []);

  const loadPreview = async (batchId) => {
    const sequence = ++previewSequence.current;
    setPreviewLoading(true);
    setPreviewError(false);
    setPreview(null);
    try {
      const response = await getApi({ path: `admin/wallets/deposit/bulk/${encodeURIComponent(batchId)}/cancellation-preview` });
      if (sequence === previewSequence.current) setPreview(response?.data?.result);
    } catch (error) {
      if (sequence === previewSequence.current) setPreviewError(true);
    } finally {
      if (sequence === previewSequence.current) setPreviewLoading(false);
    }
  };
  const open = (record) => {
    setSelected(record); setReason(""); setConfirmed(false);
    dialog.current.showModal();
    loadPreview(record._id);
  };
  const close = () => {
    if (busy) return;
    previewSequence.current += 1;
    dialog.current.close(); setSelected(null); setPreview(null);
  };
  const cancel = async () => {
    if (busy || !confirmed || !preview?.canCancel || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const response = await postApi({ path: `admin/wallets/deposit/bulk/${encodeURIComponent(selected._id)}/cancel`, body: JSON.stringify({ reason: reason.trim() }) });
      successMessage(response?.data?.result?.alreadyCancelled ? "این واریز قبلاً لغو شده است" : "کل واریز گروهی لغو شد؛ برداشت‌های برگشتی ثبت شدند");
      dialog.current.close(); setSelected(null); previewSequence.current += 1;
      load();
    } catch (error) {
      errorMessage(error?.response?.data?.errors?.[0]?.message || "لغو انجام نشد؛ دوباره بررسی کنید");
      setConfirmed(false);
      loadPreview(selected._id);
      load();
    } finally { setBusy(false); }
  };
  const rows = records.map(record => ({ ...record,
    identity: <Link className="text-blue-700 dark:text-blue-300" to={`/wallets-transaction?batchId=${encodeURIComponent(record._id)}`} title={record._id}><bdi>{record._id.slice(0, 8)}</bdi></Link>,
    date: convertDate(record.createdAt, true), admin: record.performedByUser?.name || record.performedByUser?.mobile || "—",
    audience: <span>{modes[record.mode] || "واریز گروهی"}{record.mode === "roles" && <small className="block">{record.roles.map(role => Object.values(utils?.ROLES || {}).find(option => option.value === role)?.name || role).join("، ")}</small>}{record.mode === "exceptUsers" && <small className="block">{number(record.userIds.length)} نفر مستثنا</small>}</span>,
    recipientCount: number(record.count), individualAmount: number(record.amount), total: number(record.totalAmount),
    state: <span className={`inline-block rounded-full px-3 py-1 ${record.status === "cancelled" ? "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-200" : "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-200"}`}>{record.status === "cancelled" ? "لغوشده" : "انجام‌شده"}</span>,
  }));
  return <section className="rounded-3xl bg-white p-4 dark:bg-gray-900 sm:p-6">
    <Title title="سوابق واریز گروهی کیف پول" />
    <p className="my-4 text-sm text-gray-600 dark:text-gray-300">لغو کل واریز با ثبت برداشت برگشتی انجام می‌شود. اگر موجودی قابل برداشت حتی یک نفر کافی نباشد، هیچ واریزی لغو نمی‌شود.</p>
    <Link to="/wallets/bulk-deposit" className="inline-block rounded-lg bg-blue-700 px-4 py-2 text-white">واریز گروهی جدید</Link>
    {batchId && <Link to="/wallets/bulk-history" className="mr-4 text-blue-700 dark:text-blue-300">نمایش همهٔ سوابق</Link>}
    <Table columns={columns} rows={rows} loading={request.loading} error={request.error} onRetry={request.retry} onCustomFunction={open} customFunctionName="جزئیات و لغو" />
    <Pagination pagination={pagination} filterDataHandler={({ page: nextPage }) => setPage(nextPage)} />
    <dialog ref={dialog} onCancel={event => { event.preventDefault(); close(); }} aria-labelledby="bulk-cancel-title" className="w-[calc(100%-2rem)] max-w-2xl rounded-2xl bg-white p-0 text-gray-900 shadow-xl backdrop:bg-black/50 dark:bg-gray-900 dark:text-white">
      {selected && <div className="max-h-[85dvh] overflow-y-auto p-5 sm:p-6">
        <div className="flex items-center justify-between gap-4"><h2 id="bulk-cancel-title" className="text-lg font-bold">جزئیات و لغو کل واریز</h2><button type="button" disabled={busy} onClick={close} aria-label="بستن" className="p-2 text-xl">×</button></div>
        <p className="my-3 break-all text-sm">شناسه: <bdi>{selected._id}</bdi></p>
        <p>{selected.description}</p>
        <p className="my-3">{number(selected.count)} نفر · مبلغ کل: {number(selected.totalAmount)} تومان</p>
        <Link className="text-blue-700 dark:text-blue-300" to={`/wallets-transaction?batchId=${encodeURIComponent(selected._id)}`}>نمایش تراکنش‌ها و دریافت‌کنندگان</Link>
        {selected.metadata?.status === "cancelled" && <div className="my-4 rounded-lg bg-gray-100 p-3 dark:bg-gray-800"><p>لغو در {convertDate(selected.metadata.cancelledAt, true)}</p><p>لغوکننده: {selected.cancellationAdmin?.name || selected.cancellationAdmin?.mobile || "—"}</p><p>دلیل: {selected.metadata.cancellationReason}</p></div>}
        {previewLoading && <p role="status" className="my-5">در حال بررسی موجودی دریافت‌کنندگان…</p>}
        {previewError && <div role="alert" className="my-5"><p>بررسی موجودی ناموفق بود.</p><button type="button" className="mt-2 text-blue-700" onClick={() => loadPreview(selected._id)}>تلاش دوباره</button></div>}
        {preview?.status === "cancelled" && <p role="status" className="my-5 text-red-700 dark:text-red-300">این واریز قبلاً لغو شده است.</p>}
        {preview?.status !== "cancelled" && preview?.insufficientUsers?.length > 0 && <div role="alert" className="my-5 rounded-lg border border-amber-300 p-3">
          <p className="font-medium">لغو امکان‌پذیر نیست؛ هیچ مبلغی برداشت نمی‌شود.</p>
          <p className="my-2 text-sm">موجودی قابل برداشت با کسر مبالغ رزروشدهٔ سفارش‌ها محاسبه می‌شود.</p>
          <ul className="max-h-60 space-y-3 overflow-y-auto">{preview.insufficientUsers.map(user => <li key={user.userId} className="border-t pt-2 text-sm"><p>{user.name || "بدون نام"} · <bdi>{user.mobile || user.userId}</bdi></p><p>{user.reason} · موجودی: {number(user.balance)} · مبلغ لازم: {number(user.amount)} تومان</p></li>)}</ul>
        </div>}
        {preview?.canCancel && <div className="mt-5 space-y-4">
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-100">از کیف پول هر دریافت‌کننده، مبلغ واریز اولیه برداشت می‌شود. سوابق واریز حفظ خواهد شد. موجودی هنگام تأیید نهایی دوباره بررسی می‌شود.</p>
          <label className="block">دلیل لغو (الزامی، حداقل ۳ نویسه)<textarea value={reason} disabled={busy} maxLength={500} onChange={event => setReason(event.target.value)} className="mt-2 block w-full rounded-lg border p-3 dark:bg-gray-800" rows={3} /></label>
          <label className="flex items-start gap-2"><input type="checkbox" checked={confirmed} disabled={busy} onChange={event => setConfirmed(event.target.checked)} className="mt-1" /><span>لغو واریز برای همهٔ {number(preview.count)} دریافت‌کننده و برداشت مجموع {number(preview.totalAmount)} تومان را تأیید می‌کنم.</span></label>
          <button type="button" onClick={cancel} disabled={busy || !confirmed || reason.trim().length < 3} className="rounded-lg bg-red-700 px-5 py-2.5 text-white disabled:opacity-50">{busy ? "در حال لغو…" : "لغو کل واریز گروهی"}</button>
        </div>}
        <button type="button" onClick={close} disabled={busy} className="mt-5 rounded-lg border px-4 py-2 disabled:opacity-50">بستن</button>
      </div>}
    </dialog>
  </section>;
}
